import {
  BRIDGE_SEATS,
  type BridgeCard,
  type BridgeSeat,
  type BridgeSide,
  NEW_WHIST_SCORE,
  SINGLE_PLAYER,
  type Tournament,
  type WhistGame,
  type WhistLength,
  type WhistResult,
  type WhistScore,
  addDeal,
  gameWinner,
  newTournament,
  newWhistGame,
  parseTournament,
  recordTournamentGame,
  seededRandom,
  serializeTournament,
  tournamentStatus,
  whistChooseCard,
  whistPlayCard,
  whistResult,
  whistViewFor,
} from '@strategy4kids/game-engine';
import { create } from 'zustand';
import type { AppQuery } from '../lib/query.js';
import { setAmbientMood } from '../services/audio.js';
import { cueCardPlay, cueDeal, cueRoundOrGame, cueSweep, cueTurn } from './sfx-cues.js';
import { CHAT_BRIDGE, SIDE_SEATS, chatGameEnd, chatRound, chatTrick, chatterNewGame, robotsOf } from './table-chatter.js';

/**
 * The Whist store (single device: you in South, a robot partner in North, robot opponents). Its own
 * store, like Bridge's, because nothing here is FEN-shaped. Robots decide from `whistViewFor` only.
 *
 * A game is a run of deals: points add up until a side reaches the target (short whist is 5, the
 * default; long whist 10). On top of that sits an optional weekend scoreboard (a "tournament": a
 * session is first to 10 game wins, a series first to 3 sessions), kept in local storage on this
 * device and never sent anywhere.
 */
export const HUMAN_SEAT: BridgeSeat = 'S';
export const MY_SIDE: BridgeSide = 'NS';
export const TOURNAMENT_KEY = 's4k:whist-tournament';
const SIDES: readonly BridgeSide[] = ['NS', 'EW'];

export type WhistStrength = 'easy' | 'clever';

/** Pause before a robot plays, so a kid can follow the trick. Zero in tests and `?engine=fake`. */
const ROBOT_DELAY_MS = 650;

export interface WhistState {
  game: WhistGame | null;
  /** The finished deal's result, until the next deal starts. */
  result: WhistResult | null;
  /** Deals dealt so far in this game, counting the current one (0 = no game). */
  dealNumber: number;
  score: WhistScore;
  length: WhistLength;
  /** Set when a side has reached the target: the game is over. */
  gameWinner: BridgeSide | null;
  strength: WhistStrength;
  fourColour: boolean;
  /** The seed of the game, shown so it can be replayed; each deal derives its own. */
  seed: number | null;
  query: AppQuery | null;
  /** The weekend scoreboard, or null when none is being kept. */
  tournament: Tournament<BridgeSide> | null;
  start(query: AppQuery, seed?: number): void;
  nextDeal(): void;
  newGame(): void;
  stop(): void;
  play(card: BridgeCard): void;
  setStrength(strength: WhistStrength): void;
  setFourColour(on: boolean): void;
  setLength(length: WhistLength): void;
  startTournament(): void;
  endTournament(): void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
const clearTimer = () => {
  if (timer) clearTimeout(timer);
  timer = null;
};

const freshSeed = () => Math.floor(Math.random() * 2 ** 31);
/** Each deal of a game has its own seed, derived from the game's, so a seeded game replays exactly. */
export const dealSeed = (seed: number, dealIndex: number): number => (seed + Math.imul(dealIndex, 7919)) >>> 0;
/** The dealer rotates every deal. */
export const dealerFor = (dealIndex: number): BridgeSeat => BRIDGE_SEATS[dealIndex % 4] as BridgeSeat;

function loadTournament(): Tournament<BridgeSide> | null {
  try {
    return parseTournament(localStorage.getItem(TOURNAMENT_KEY), SIDES);
  } catch {
    return null;
  }
}
function saveTournament(t: Tournament<BridgeSide> | null): void {
  try {
    if (t) localStorage.setItem(TOURNAMENT_KEY, serializeTournament(t));
    else localStorage.removeItem(TOURNAMENT_KEY);
  } catch {
    // Storage can be blocked or full (private windows): the scoreboard then lasts for this visit only.
  }
}

export const useWhistStore = create<WhistState>((set, get) => {
  /** If a robot is to play, let it, after a short pause; stops at the human's turn or the end of the deal. */
  const driveRobots = () => {
    clearTimer();
    const { game, query, strength } = get();
    if (!game || game.phase === 'done' || game.controllers[game.turn] !== 'ai') return;
    const delay = query?.engine === 'fake' ? 0 : ROBOT_DELAY_MS;
    timer = setTimeout(() => {
      const current = get().game;
      if (!current || current.phase === 'done' || current.controllers[current.turn] !== 'ai') return;
      const card = whistChooseCard(
        whistViewFor(current, current.turn),
        strength,
        seededRandom(current.seed + current.tricks.length * 4 + current.trick.length),
      );
      advance(whistPlayCard(current, card));
      driveRobots();
    }, delay);
  };

  /** Take a new game state; when it ends the deal, score it, and when that ends the game, record it. */
  const advance = (game: WhistGame) => {
    const prev = get().game;
    // Every card is heard from the seat that played it (spec P2), robots included, then the sweep.
    if (prev?.phase === 'play') {
      cueCardPlay(prev.turn);
      cueSweep(prev, game);
      chatTrick(prev, game, robotsOf(BRIDGE_SEATS), CHAT_BRIDGE);
    }
    const result = whistResult(game);
    if (!result) {
      set({ game });
      cueTurn(prev?.phase === 'play' ? prev.turn : null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
      return;
    }
    const { score: before, length, tournament } = get();
    const score = addDeal(before, result);
    const winner = gameWinner(score, length);
    let next = tournament;
    if (winner && tournament && tournamentStatus(tournament).state === 'playing') {
      next = recordTournamentGame(tournament, [winner]);
      saveTournament(next);
    }
    set({ game, result, score, gameWinner: winner, tournament: next });
    // The game-ending deal gets the game's win/lose; any other deal gets the round stinger.
    const mine = (winner ?? result.winningSide) === MY_SIDE;
    cueRoundOrGame(mine ? 'won' : 'lost', winner !== null);
    const winSide = winner ?? result.winningSide;
    const loseSide = winSide === 'NS' ? 'EW' : 'NS';
    const robots = robotsOf(BRIDGE_SEATS);
    if (winner) chatGameEnd(SIDE_SEATS[winSide], robots, CHAT_BRIDGE);
    else chatRound(robots.find((r) => SIDE_SEATS[winSide].includes(r)) ?? null, robots.find((r) => SIDE_SEATS[loseSide].includes(r)) ?? null, CHAT_BRIDGE);
  };

  const deal = (dealIndex: number) => {
    clearTimer();
    const { seed } = get();
    if (seed === null) return;
    const game = newWhistGame({ seed: dealSeed(seed, dealIndex), dealer: dealerFor(dealIndex), controllers: SINGLE_PLAYER });
    set({ game, result: null, dealNumber: dealIndex + 1 });
    cueDeal();
    cueTurn(null, game.phase === 'play' ? game.turn : null, HUMAN_SEAT);
    driveRobots();
  };

  const beginGame = (query: AppQuery, seed: number, keepTournament: boolean) => {
    clearTimer();
    chatterNewGame();
    set({
      seed,
      query,
      score: NEW_WHIST_SCORE,
      gameWinner: null,
      result: null,
      dealNumber: 0,
      tournament: keepTournament ? get().tournament : loadTournament(),
    });
    deal(0);
  };

  return {
    game: null,
    result: null,
    dealNumber: 0,
    score: NEW_WHIST_SCORE,
    length: 'short',
    gameWinner: null,
    strength: 'easy',
    fourColour: false,
    seed: null,
    query: null,
    tournament: null,
    start: (query, seed) => {
      setAmbientMood('cards');
      beginGame(query, seed ?? query.deal ?? freshSeed(), false);
    },
    nextDeal: () => {
      const { result, gameWinner: winner, dealNumber } = get();
      if (!result || winner) return;
      deal(dealNumber);
    },
    newGame: () => {
      const { query } = get();
      if (query) beginGame(query, query.deal ?? freshSeed(), true);
    },
    stop: () => {
      clearTimer();
      set({ game: null, result: null, seed: null, dealNumber: 0, score: NEW_WHIST_SCORE, gameWinner: null });
    },
    play: (card) => {
      const { game } = get();
      if (!game || game.phase !== 'play' || game.turn !== HUMAN_SEAT) return;
      const legal = whistViewFor(game, HUMAN_SEAT).legalPlays;
      if (!legal.some((c) => c.suit === card.suit && c.rank === card.rank)) return;
      advance(whistPlayCard(game, card));
      driveRobots();
    },
    setStrength: (strength) => set({ strength }),
    setFourColour: (fourColour) => set({ fourColour }),
    setLength: (length) => set({ length }),
    startTournament: () => {
      if (get().tournament) return;
      const tournament = newTournament(SIDES);
      saveTournament(tournament);
      set({ tournament });
    },
    endTournament: () => {
      saveTournament(null);
      set({ tournament: null });
    },
  };
});
